<template>
  <div :class="$style.board">
    <header :class="$style.header">
      <div :class="$style.headerInfo">
        <div :class="$style.stageInfo">
          <h2 :class="$style.title">{{ stage.name }}</h2>
          <pickem-countdown
            v-if="timerTo(stage)"
            :to="timerTo(stage)"
            :class="$style.timerChip"
          >
            <ui-icon name="clock-filled" :class="$style.timerIcon" />
          </pickem-countdown>
          <stage-condition-tag v-bind="stageConditionTag" />
        </div>
        <p v-if="subtitle" :class="$style.subtitle">{{ subtitle }}</p>
      </div>

      <pick-result-toggle
        v-if="canToggle"
        v-model="modeRef"
        :class="$style.toggle"
      />
    </header>

    <unlock-banner
      v-if="showUnlockBanner"
      :stage="stage"
      :unlock="stage.unlock"
      is-compact
    />

    <teams-palette
      v-if="!isResultsView && stage.teams.length"
      :teams="stage.teams"
      :placed-team-ids="placedTeamIds"
      :selected-team-id="selectedTeam?.id ?? null"
      :disabled="disabled"
      @select-team="selectTeam"
      @deselect-team="deselectTeam"
      :class="$style.palette"
    />

    <div :class="$style.categories">
      <category-card
        v-for="category in stage.swiss.categories"
        :key="category.id"
        :category="category"
        :teams-by-id="teamsById"
        :predictions="stage.swiss.predictions"
        :in-flight-team-ids="inFlightTeamIds"
        :locked="locked"
        :disabled="disabled"
        :compact-mode="compactMode"
        :results-view="isResultsView"
        :results="stage.swiss.results"
        :summary="summaryFor(category)"
        :ui-state="stage.ui_state"
        @slot-click="onSlotClick($event, category)"
        @drop-team="onDropTeam"
        @remove-team="onRemoveTeam"
      />
    </div>

    <eliminated-teams
      v-if="isResultsView && eliminatedTeamIds.length"
      :team-ids="eliminatedTeamIds"
      :teams-by-id="teamsById"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import UiIcon from '@/ui-core/components/icon/index.vue'
import TeamsPalette from './TeamsPalette.vue'
import CategoryCard from './CategoryCard.vue'
import EliminatedTeams from './EliminatedTeams.vue'
import PickResultToggle, { type PickResultMode } from './PickResultToggle.vue'
import UnlockBanner from '../unlock/UnlockBanner.vue'
import PickemCountdown from '../../common/Countdown.vue'
import StageConditionTag from '../StageConditionTag.vue'
import {
  getStageConditionTag,
  timerTo
} from '../../../utils/get-stage-condition-tag'
import { usePredictionsStore } from '../../../stores/predictions'
import { useTournamentStore } from '../../../stores/tournament'
import {
  usePickemAnalytics,
  mapSwissOutcomeGroup,
  type PickemPickAnalyticsMeta
} from '../../../composables/use-pickem-analytics'
import { swissCategoryLabel } from '../../../utils/swiss-category-label'
import type { Stage, PickemTeam, SwissCategory } from '../../../types'

defineOptions({ name: 'PickemSwissBoard' })

const props = defineProps<{
  stage: Stage & { swiss: NonNullable<Stage['swiss']> }
  compactMode?: boolean
  showUnlockBanner?: boolean
}>()

const { $dialog, $matchMedia, $snacks } = useNuxtApp()

const predictionsStore = usePredictionsStore()
const tournamentStore = useTournamentStore()
const { trackSwitch, trackPickClick } = usePickemAnalytics()

const lockedByUnlockConditions = computed(
  () =>
    Boolean(props.stage.unlock?.required) && !props.stage.unlock?.user_unlocked
)

const locked = computed(
  () =>
    props.stage.ui_state === 'awaiting_results' ||
    props.stage.ui_state === 'awaiting_open' ||
    lockedByUnlockConditions.value
)

const disabled = computed(
  () => props.stage.ui_state !== 'pickable' || lockedByUnlockConditions.value
)

const stageConditionTag = computed(() => getStageConditionTag(props.stage))

const canToggle = computed(() => props.stage.result_view_available)

// Режим вида: pick / result. По дефолту:
//   - finished/awaiting_results → result
//   - всё остальное              → pick
const modeRef = ref<PickResultMode>(
  canToggle.value && props.stage.ui_state === 'finished' ? 'result' : 'pick'
)
const skipSwitchTrack = ref(false)

watch(
  () => props.stage.ui_state,
  newState => {
    skipSwitchTrack.value = true
    if (newState === 'finished') {
      modeRef.value = 'result'
    } else {
      modeRef.value = 'pick'
    }
  }
)

watch(modeRef, (value, previousValue) => {
  if (skipSwitchTrack.value) {
    skipSwitchTrack.value = false
    return
  }
  if (value === previousValue) return

  if (!tournamentStore.tournament) return

  trackSwitch({
    tabValue: value,
    stage: props.stage,
    tournament: tournamentStore.tournament
  })
})

const isResultsView = computed(
  () => canToggle.value && modeRef.value === 'result'
)

// teamsById для быстрых лукапов внутри карточек.
const teamsById = computed<Record<number, PickemTeam>>(() => {
  const map: Record<number, PickemTeam> = {}
  for (const team of props.stage.teams) {
    map[team.id] = team
  }
  return map
})

const placedTeamIds = computed(
  () => new Set(props.stage.swiss.predictions.map(p => p.team_id))
)

// Команды, которые сейчас «летят» — для disable-стейта.
const inFlightTeamIds = computed<ReadonlySet<number>>(() => {
  const set = new Set<number>()
  for (const team of props.stage.teams) {
    if (predictionsStore.isSwissInFlight(props.stage.id, team.id)) {
      set.add(team.id)
    }
  }
  return set
})

// Eliminated teams — приходят отдельным массивом от бэка (см. SwissShape).
const eliminatedTeamIds = computed<number[]>(() => {
  const list = props.stage.swiss.eliminated_teams
  if (!list) return []
  return list.map(item => item.team.id)
})

// Сводка по категории для resultsView. Использует points_earned/correct_count
// прямо с бэка, чтобы не дублировать логику.
const summaryFor = (category: SwissCategory) => {
  if (isResultsView.value) return null
  if (
    category.points_earned == null &&
    category.correct_count == null &&
    category.picks_count == null
  ) {
    return null
  }
  return {
    points: category.points_earned ?? 0,
    correct: category.correct_count ?? 0,
    total: category.picks_count ?? 0
  }
}

// «Подсветка»: subtitle над тогглером.
const subtitle = computed(() => {
  switch (props.stage.ui_state) {
    case 'awaiting_results':
      return 'Выбор недоступен, этап уже начался'
    case 'finished':
      return 'Этап завершён'
    case 'pickable':
      return null
    default:
      return null
  }
})

// =====================================================================
// Выбор команды → клик по слоту = upsert (десктоп flow).
// =====================================================================

const selectedTeam = ref<PickemTeam | null>(null)

const selectTeam = (team: PickemTeam) => {
  selectedTeam.value = team
}

const deselectTeam = () => {
  selectedTeam.value = null
}

const isMobileLayout = computed(() => {
  // true → ширина <= md, мобильная вёрстка.
  if (!$matchMedia) return false
  return $matchMedia.down('md', false)
})

const onSlotClick = async (
  payload: { index: number; team: PickemTeam | null },
  category: SwissCategory
) => {
  if (disabled.value || isResultsView.value) return

  // Мобила: тап по слоту открывает picker (вне зависимости от выбранной команды).
  if (isMobileLayout.value) {
    openTeamPicker(category)
    return
  }

  // Десктоп: если есть выбранная команда — кладём её в слот.
  if (selectedTeam.value) {
    const teamToPlace = selectedTeam.value
    selectedTeam.value = null
    await placeTeam(teamToPlace, category, payload.index + 1)
    return
  }

  // Десктоп без выбранной команды и тап по пустому слоту — открываем picker.
  if (!payload.team) {
    openTeamPicker(category)
  }
}

const onDropTeam = async ({
  teamId,
  categoryId
}: {
  teamId: number
  categoryId: number
}) => {
  if (disabled.value || isResultsView.value) return
  const team = teamsById.value[teamId]
  const category = props.stage.swiss.categories.find(c => c.id === categoryId)
  if (!team || !category) return
  await placeTeam(team, category)
}

const getSwissSlotIndex = (category: SwissCategory, teamId: number) => {
  const predictionsInCategory = props.stage.swiss.predictions.filter(
    prediction => prediction.category_id === category.id
  )
  const existingIndex = predictionsInCategory.findIndex(
    prediction => prediction.team_id === teamId
  )

  if (existingIndex >= 0) return existingIndex + 1
  return predictionsInCategory.length + 1
}

const buildSwissPickMeta = (
  team: PickemTeam,
  category: SwissCategory,
  pickAction: PickemPickAnalyticsMeta['pick_action'],
  slotIndex?: number
): PickemPickAnalyticsMeta => {
  const picksCountAfter =
    pickAction === 'remove'
      ? props.stage.slots_filled - 1
      : pickAction === 'add'
        ? props.stage.slots_filled + 1
        : props.stage.slots_filled

  return {
    pick_action: pickAction,
    pick_outcome_group: mapSwissOutcomeGroup(category),
    pick_slot_index: slotIndex ?? getSwissSlotIndex(category, team.id),
    team,
    stage: props.stage,
    picks_count_after: picksCountAfter
  }
}

const onRemoveTeam = async (teamId: number) => {
  if (disabled.value || isResultsView.value) return
  const team = teamsById.value[teamId]
  if (!team) return

  const prediction = props.stage.swiss.predictions.find(
    item => item.team_id === teamId
  )
  const category = prediction
    ? props.stage.swiss.categories.find(
        item => item.id === prediction.category_id
      )
    : null
  if (!category) return

  const analyticsMeta = buildSwissPickMeta(team, category, 'remove')
  if (!tournamentStore.tournament) return

  trackPickClick(analyticsMeta, tournamentStore.tournament)

  try {
    await predictionsStore.removeSwiss({
      stage: props.stage,
      team,
      analyticsMeta
    })
    await predictionsStore.removeSwiss({ stage: props.stage, team })
  } catch (error) {
    showError(error, 'Не удалось убрать команду')
  }
}

const placeTeam = async (
  team: PickemTeam,
  category: SwissCategory,
  slotIndex?: number
) => {
  // Защита: если slots_count уже заполнен (учитывая что та же team тоже
  // занимает место — её исключаем при переносе) — не пихаем.
  if (category.slots_count > 0) {
    const filled = props.stage.swiss.predictions.filter(
      p => p.category_id === category.id && p.team_id !== team.id
    ).length
    if (filled >= category.slots_count) {
      $snacks?.error?.('В категории нет свободных слотов')
      return
    }
  }

  try {
    const existingPrediction = props.stage.swiss.predictions.find(
      item => item.team_id === team.id
    )
    const pickAction: PickemPickAnalyticsMeta['pick_action'] =
      existingPrediction ? 'replace' : 'add'
    const analyticsMeta = buildSwissPickMeta(
      team,
      category,
      pickAction,
      slotIndex
    )

    if (!tournamentStore.tournament) return

    trackPickClick(analyticsMeta, tournamentStore.tournament)

    await predictionsStore.upsertSwiss({
      stage: props.stage,
      team,
      category,
      analyticsMeta
    })
  } catch (error) {
    showError(error, 'Не удалось сохранить пик')
  }
}

const openTeamPicker = (category: SwissCategory) => {
  const teams = props.stage.teams.filter(
    team => !placedTeamIds.value.has(team.id)
  )
  const disabledTeamIds = inFlightTeamIds.value
  const TeamPickerDialog = defineAsyncComponent(
    () => import('./TeamPickerDialog.vue')
  )

  const subtitleText = `Категория «${swissCategoryLabel(category)}»`

  $dialog({
    content: TeamPickerDialog,
    scrollLock: false,
    contentProps: {
      teams,
      disabledTeamIds,
      title: 'Выбор команды',
      subtitle: subtitleText,
      onPick: async (team: PickemTeam) => {
        await placeTeam(team, category)
      }
    }
  })
}

const showError = (error: unknown, fallback: string) => {
  // Минимальная обработка для итерации 2 — детальный mapping в iter8.
  const message = (error as Error)?.message || fallback
  if (typeof console !== 'undefined') {
    console.warn('[pickem] swiss mutation failed:', message)
  }
  $snacks?.error?.(fallback)
}
</script>

<style lang="scss" module>
.board {
  display: flex;
  flex-direction: column;
  gap: 1.6em;
  padding: 2.4em;
  background-color: color-get(--root-rgb-bg-tone-dark);
  border-radius: 0 0 1.2em 1.2em;

  @include down(md) {
    gap: 2em;
    padding: 0 1.6em 0;
    border-radius: 0;
    background: rgba(var(--root-rgb-bg-special-1), 1);
  }
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.6em;
  flex-wrap: wrap;

  @include down(md) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.8em;
  }
}

.headerInfo {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
}

.stageInfo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.2em;
}

.timerChip {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.4em 0.6em;
  border-radius: 0.4em;
  background-color: color-get(--root-rgb-accent-2);
  color: color-get(--root-rgb-text-inverse);
  @include typo-get(subhead-s);
  white-space: nowrap;
}

.timerIcon {
  width: 0.9em;
  flex-shrink: 0;
}

.title {
  margin: 0;
  @include typo-get(headline-s);
  color: color-get(--root-rgb-text-1);

  @include down(md) {
    @include typo-get(subhead-l);
  }
}

.subtitle {
  margin: 0;
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-2);
}

.toggle {
  flex-shrink: 0;

  @include down(md) {
    width: 100%;
  }
}

.palette {
  width: 100%;

  @include down(md) {
    display: none;
  }
}

.categories {
  display: grid;
  grid-template-columns: repeat(2, minmax(28em, 1fr));
  gap: 2em;

  > :last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  @include down(md) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.2em;
  }
}
</style>
